import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaStar, FaEye, FaCopy, FaEdit, FaTrash, FaCodeBranch, FaUser } from 'react-icons/fa';
import { Badge } from '../ui';
import BookmarkButton from '../BookmarkButton';
import ReportButton from '../ReportButton';
import ShareButton from '../ShareButton';
import SourceAttribution from '../SourceAttribution';
import toast from 'react-hot-toast';

/**
 * PromptHeader - Separated header section from PromptDetail
 * Contains title, metadata, actions
 */
const PromptHeader = ({ prompt, currentUser, isOwner, onDelete, onFork, onClaim, canClaim }) => {
  const navigate = useNavigate();

  const handleCopyPrompt = () => {
    const plainText = prompt.prompt_text.replace(/<[^>]*>/g, '');
    navigator.clipboard.writeText(plainText);
    toast.success('Prompt copied to clipboard!');
  };

  return (
    <div className="bg-white dark:bg-spooky-bg-medium p-6 rounded-lg shadow-md space-y-4">
      {/* Title & Actions */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold mb-2 dark:text-white break-words">{prompt.title}</h1>
          {prompt.description && (
            <p className="text-gray-600 dark:text-gray-300">{prompt.description}</p>
          )}
        </div>
        {isOwner && (
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => navigate(`/prompts/${prompt.id}/edit`)}
              className="p-2 text-boop-primary hover:bg-boop-light dark:hover:bg-spooky-bg-light rounded-lg transition"
              title="Edit"
            >
              <FaEdit />
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
              title="Delete"
            >
              <FaTrash />
            </button>
          </div>
        )}
      </div>

      {/* Metadata Row */}
      <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
        <Link
          to={`/profile/${prompt.username}`}
          className="flex items-center hover:text-boop-primary dark:hover:text-boop-primary transition gap-2"
        >
          <FaUser />
          <span>@{prompt.username}</span>
        </Link>
        <span className="flex items-center gap-2">
          <FaEye />
          {prompt.views_count || 0} views
        </span>
        <span className="flex items-center text-boop-primary dark:text-boop-primary font-semibold gap-2">
          <FaStar />
          {(parseFloat(prompt.bayesian_rating) || 0).toFixed(1)} ({prompt.total_ratings || 0} ratings)
        </span>
      </div>

      {/* Badges Row */}
      <div className="flex gap-2 flex-wrap">
        <Badge variant="info">{prompt.token_count || 0} tokens</Badge>
        <Badge variant="primary">{prompt.target_ai_model || 'N/A'}</Badge>
        {prompt.tags?.map((tag, i) => (
          <Badge key={i} variant="default" className="bg-boop-light text-boop-primary">
            {tag}
          </Badge>
        ))}
      </div>

      {/* Source Attribution */}
      {(prompt.source_platform || prompt.source_url) && (
        <SourceAttribution prompt={prompt} />
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap pt-2">
        <button
          onClick={handleCopyPrompt}
          className="flex items-center gap-2 px-4 py-2 bg-boop-primary text-white rounded-lg hover:bg-boop-secondary transition font-medium"
        >
          <FaCopy />
          Copy Prompt
        </button>
        <ShareButton prompt={prompt} />
        <BookmarkButton promptId={prompt.id} />
        {currentUser && !isOwner && (
          <>
            <ReportButton promptId={prompt.id} promptTitle={prompt.title} />
            <button
              onClick={onFork}
              className="flex items-center gap-2 px-4 py-2 border border-boop-primary text-boop-primary rounded-lg hover:bg-boop-light dark:hover:bg-spooky-bg-light transition font-medium"
            >
              <FaCodeBranch />
              Fork
            </button>
          </>
        )}
        {canClaim() && (
          <button
            onClick={onClaim}
            className="flex items-center gap-2 px-4 py-2 border border-amber-500 text-amber-700 dark:text-amber-400 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition font-medium"
            title="If this is your original prompt from the source, claim it."
          >
            Claim this prompt
          </button>
        )}
      </div>
    </div>
  );
};

export default PromptHeader;
